
import { supabase } from "@/integrations/supabase/client";
import { checkAdminStatus, checkSchoolUserStatus, createSchoolUser } from "./auth-db";
import { SchoolUserData } from "@/types/auth";
import { NavigateFunction } from "react-router-dom";

interface ToastFunction {
  (props: { title: string; description: string; variant?: "default" | "destructive" }): void;
}

export const handleSignIn = async (
  email: string, 
  password: string, 
  navigate: NavigateFunction,
  toast: ToastFunction,
  setLoading: (loading: boolean) => void
) => {
  setLoading(true);
  try {
    console.log('Attempting sign in for:', email);

    // First attempt authentication
    const { data, error: signInError } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    if (signInError) {
      console.error('Sign in error:', signInError);
      if (signInError.message.includes("Invalid login credentials")) {
        toast({
          title: "Login Gagal",
          description: "Email atau password yang Anda masukkan salah. Silakan coba lagi.",
          variant: "destructive",
        });
      } else {
        toast({
          title: "Error",
          description: `${signInError.message}`,
          variant: "destructive",
        });
      }
      setLoading(false);
      return;
    }

    if (data.user) {
      // After successful authentication, check user type
      const isAdmin = await checkAdminStatus(email);
      const schoolUser = await checkSchoolUserStatus(email);

      if (!isAdmin && !schoolUser) {
        toast({
          title: "Akun tidak ditemukan",
          description: "Email ini belum terdaftar sebagai pengguna. Silakan daftar terlebih dahulu.",
          variant: "destructive",
        });
        // Sign out since the user isn't authorized
        await supabase.auth.signOut();
        setLoading(false);
        return;
      }

      // For school users, check if they're approved
      if (!isAdmin && schoolUser && !schoolUser.approved) {
        toast({
          title: "Akun Belum Disetujui",
          description: "Akun Anda sedang menunggu persetujuan admin. Silakan tunggu atau hubungi admin.",
          variant: "destructive",
        });
        // Sign out since the user isn't approved yet
        await supabase.auth.signOut();
        setLoading(false);
        return;
      }

      // Navigate based on user type
      if (isAdmin) {
        console.log('Redirecting admin to admin dashboard');
        navigate("/admin/dashboard");
      } else {
        console.log('Redirecting user to user dashboard');
        navigate("/dashboard");
      }

      toast({
        title: "Login Berhasil",
        description: "Selamat datang kembali!",
      });
    }
  } catch (error) {
    console.error('Login error:', error);
    toast({
      title: "Error",
      description: "Terjadi kesalahan saat login. Silakan coba lagi.",
      variant: "destructive",
    });
  } finally {
    setLoading(false);
  }
};

export const handleSignUp = async (
  email: string, 
  password: string, 
  userData: SchoolUserData = {},
  toast: ToastFunction,
  setLoading: (loading: boolean) => void
): Promise<{ success: boolean }> => {
  setLoading(true);
  try {
    // Check if user already exists in school_users
    const existingUser = await checkSchoolUserStatus(email);

    if (existingUser) {
      toast({
        title: "Email sudah terdaftar",
        description: "Silakan login atau gunakan email lain untuk mendaftar.",
        variant: "destructive",
      });
      return { success: false };
    }

    // Validate school ID before proceeding with registration
    if (!userData.school_id) {
      toast({
        title: "ID Sekolah Diperlukan",
        description: "Silakan masukkan ID Sekolah untuk mendaftar.",
        variant: "destructive",
      });
      return { success: false };
    }

    // Check if school exists
    const { data: schoolData, error: schoolError } = await supabase
      .from('clients')
      .select('school_id')
      .eq('school_id', userData.school_id)
      .maybeSingle();

    if (schoolError || !schoolData) {
      toast({
        title: "ID Sekolah Tidak Valid",
        description: "ID Sekolah yang Anda masukkan tidak terdaftar dalam sistem. Silakan periksa kembali.",
        variant: "destructive",
      });
      return { success: false };
    }

    // Create auth user first
    const { data, error: signUpError } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: `${window.location.origin}/auth`,
        data: {
          school_id: userData.school_id,
          role: userData.role
        }
      }
    });
    
    if (signUpError) {
      toast({
        title: "Pendaftaran Gagal",
        description: signUpError.message,
        variant: "destructive",
      });
      return { success: false };
    }

    // If auth user creation successful, create school user entry
    await createSchoolUser(email, userData);
    
    toast({
      title: "Pendaftaran Berhasil!",
      description: "Silakan periksa email Anda untuk konfirmasi pendaftaran.",
    });
    
    return { success: true };
  } catch (error) {
    console.error('Signup error:', error);
    toast({
      title: "Error",
      description: "Terjadi kesalahan saat mendaftar. Silakan coba lagi.",
      variant: "destructive",
    });
    return { success: false };
  } finally {
    setLoading(false);
  }
};
